import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Event } from '../../model/event';
import { RootState } from '../store';
import { ApiStatus } from '../../api/apiStatus';
import {
  completeEvent as completeEventApi,
  cancelCompleteEvent as cancelCompleteEventApi,
  deleteCamera as deleteCameraApi,
  getEvent as getEventApi,
  getEventCameras,
  getEventStatus,
  getFailedImages,
} from '../../api/eventApi';
import { AxiosResponse } from 'axios';
import {
  onCompleteEvent,
  onCompleteEventFailed,
  onCompleteEventSuccess,
  onDeleteCamera,
  onDeleteCameraFailed,
  onDeleteCameraSuccess,
} from './eventSliceReducer';
import { EventCompleteRequest } from '../../model/event-complete-request';

export interface EventState {
  events: Array<Event>;
  status: ApiStatus;
  refreshStatus: ApiStatus;
  completeStatus: ApiStatus;
  failedCameraUploadStatus: ApiStatus;
  errorMessage?: string;
  completeErrorMessage?: string;
  selectedEventId?: string;
  deleteCameraStatus: ApiStatus;
  deleteCameraErrorMessage?: string;
  completionMessage?: string;
  isImmediateCompletionAttempt?: boolean;
}

const initialState: EventState = {
  events: [],
  status: null,
  refreshStatus: null,
  completeStatus: null,
  failedCameraUploadStatus: null,
  deleteCameraStatus: null,
  completionMessage: undefined,
  isImmediateCompletionAttempt: false,
};

// setup thunk
export const loadEvent = createAsyncThunk('events/getEvent', async (eventId: string, { rejectWithValue }) => {
  try {
    return await getEventApi(eventId);
  } catch (err: any) {
    return rejectWithValue((err.response as AxiosResponse).data?.message as string);
  }
});

export const refreshEvent = createAsyncThunk('events/refreshEvent', async (eventId: string, { rejectWithValue }) => {
  try {
    const status = await getEventStatus(eventId);
    if (status === 'INITIATED' || status === 'COMPLETE') {
      const [eventDetails, eventDays] = await Promise.all([getEventApi(eventId), getEventCameras(eventId)]);
      return { eventId, eventDetails, eventDays };
    }
    const eventDays = await getEventCameras(eventId);
    return { eventId, status, eventDays };
  } catch (err: any) {
    return rejectWithValue((err.response as AxiosResponse).data?.message as string);
  }
});

export const loadFailedImageDetails = createAsyncThunk(
  'events/getFailedImages',
  async (eventId: string, { rejectWithValue }) => {
    try {
      return await getFailedImages(eventId);
    } catch (err: any) {
      return rejectWithValue((err.response as AxiosResponse).data?.message as string);
    }
  }
);

export const cancelCompleteEvent = createAsyncThunk(
  'events/cancelCompleteEvent',
  async (eventId: string, { rejectWithValue }) => {
    try {
      const response = await cancelCompleteEventApi(eventId);
      if (response.success) {
        refreshEvent(eventId);
      }
      return { eventId, response };
    } catch (err: any) {
      // Ensure a string payload for rejection
      const errorMessage =
        (err.response as AxiosResponse)?.data?.message ||
        err.message ||
        'Failed to cancel event completion (unknown error)';
      return rejectWithValue(errorMessage as string);
    }
  }
);

export const completeEvent = createAsyncThunk(
  'events/completeEvent',
  async (eventCompleteRequest: EventCompleteRequest, { rejectWithValue }) => {
    if (!eventCompleteRequest) {
      return rejectWithValue('event Complete Request is undefined');
    }
    if (!eventCompleteRequest.completionMode) {
      return rejectWithValue('eventCompletionMode is undefined');
    }
    try {
      const response = await completeEventApi(eventCompleteRequest);
      return { eventCompleteRequest, response };
    } catch (err: any) {
      return rejectWithValue((err.response as AxiosResponse).data?.message || err.message);
    }
  }
);

export const deleteCamera = createAsyncThunk('deleteCamera', async (payload: any, { rejectWithValue }) => {
  try {
    await deleteCameraApi(payload.eventId, payload.deleteRequest);
    return payload.deleteRequest;
  } catch (err: any) {
    return rejectWithValue((err.response as AxiosResponse).data?.message || err.message);
  }
});

// create slice
export const eventsSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    resetEventState: (state) => {
      state.events = initialState.events;
      state.status = initialState.status;
      state.completeStatus = initialState.completeStatus;
      state.errorMessage = initialState.errorMessage;
      state.selectedEventId = initialState.selectedEventId;
      state.completeErrorMessage = initialState.completeErrorMessage;
      state.completionMessage = initialState.completionMessage;
    },
    resetCompleteStatus: (state) => {
      state.completeStatus = null;
      state.completeErrorMessage = undefined;
      state.completionMessage = undefined;
    },
  },
  // setup extra reducers to handle actions generated by createAsyncThunk
  extraReducers: (builder) => {
    builder
      .addCase(loadEvent.pending, (state) => {
        state.status = 'LOADING';
      })
      .addCase(loadEvent.fulfilled, (state, action) => {
        state.status = 'SUCCESS';
        state.events = [action.payload];
        state.selectedEventId = action.payload.eventId;
        state.errorMessage = undefined;
      })
      .addCase(loadEvent.rejected, (state, action) => {
        state.status = 'FAILED';
        state.events = [];
        state.errorMessage = action.payload as string;
        state.selectedEventId = undefined;
      })
      .addCase(loadFailedImageDetails.pending, (state) => {
        state.failedCameraUploadStatus = 'LOADING';
      })
      .addCase(loadFailedImageDetails.fulfilled, (state, action) => {
        state.failedCameraUploadStatus = 'SUCCESS';
        state.events = state.events.map((event: Event) =>
          event.eventId === state.selectedEventId ? { ...event, failedCameraUploads: action.payload } : event
        );
        state.errorMessage = undefined;
      })
      .addCase(loadFailedImageDetails.rejected, (state, action) => {
        state.failedCameraUploadStatus = 'FAILED';
        state.events = state.events.map((event: Event) =>
          event.eventId === state.selectedEventId ? { ...event, failedCameraUploads: [] } : event
        );
        state.errorMessage = action.payload as string;
      })
      .addCase(refreshEvent.pending, (state) => {
        state.refreshStatus = 'LOADING';
      })
      .addCase(refreshEvent.fulfilled, (state, action) => {
        const { eventId, status, eventDays, eventDetails = undefined } = action.payload;
        state.refreshStatus = 'SUCCESS';
        state.events = state.events.map((event) => {
          if (event.eventId === eventId) {
            if (eventDetails) {
              return {
                ...event,
                ...eventDetails,
                days: eventDays || [],
              };
            }
            return {
              ...event,
              status: status,
              days: eventDays || [],
            };
          }
          return event;
        });
        state.errorMessage = undefined;
      })
      .addCase(refreshEvent.rejected, (state, action) => {
        state.refreshStatus = 'FAILED';
        state.errorMessage = action.payload as string;
      })
      .addCase(completeEvent.pending, (state, action) => {
        const completionMode = action.meta.arg.completionMode;
        state.isImmediateCompletionAttempt =
          completionMode === 'WEB_UPLOADER_COMPLETE' ||
          completionMode === 'USER_FORCE_COMPLETE' ||
          completionMode === 'OVERRIDE_COMPLETE';

        onCompleteEvent(state);
      })
      .addCase(completeEvent.fulfilled, onCompleteEventSuccess)
      .addCase(completeEvent.rejected, onCompleteEventFailed)
      .addCase(deleteCamera.pending, onDeleteCamera)
      .addCase(deleteCamera.fulfilled, onDeleteCameraSuccess)
      .addCase(deleteCamera.rejected, onDeleteCameraFailed)
      .addCase(cancelCompleteEvent.pending, (state) => {
        state.completeStatus = 'LOADING';
        state.completeErrorMessage = undefined;
      })
      .addCase(cancelCompleteEvent.fulfilled, (state, action) => {
        const { eventId, response } = action.payload;
        if (response.success) {
          state.completeStatus = 'SUCCESS';
          state.events = state.events.map((event) =>
            event.eventId === eventId
              ? {
                  ...event,
                  status: 'PENDING',
                  validationErrors: undefined,
                  initiatedDate: undefined,
                  initiatedUser: undefined,
                  completedBy: undefined,
                  isOverrideCompleted: undefined,
                }
              : event
          );
          state.completeErrorMessage = undefined;
          state.completionMessage = 'Event completion has been successfully canceled.';
        } else {
          state.completeStatus = 'FAILED';
          if (response.validationErrors && response.validationErrors.length > 0) {
            state.completeErrorMessage =
              response.validationErrors[0].errorMessage ||
              'Failed to cancel event completion due to validation issues.';
          } else {
            state.completeErrorMessage = 'Failed to cancel event completion.';
          }
        }
      })
      .addCase(cancelCompleteEvent.rejected, (state, action) => {
        state.completeStatus = 'FAILED';
        state.completeErrorMessage = action.payload as string;
      });
  },
});

// create selectors
export const selectStatus = (state: RootState) => ({
  status: state.event.status,
  refreshStatus: state.event.refreshStatus,
  completeStatus: state.event.completeStatus,
  failedCameraUploadStatus: state.event.failedCameraUploadStatus,
  deleteCameraStatus: state.event.deleteCameraStatus,
});
export const selectErrorMessage = (state: RootState) => state.event.errorMessage;
export const selectCompleteErrorMessage = (state: RootState) => state.event.completeErrorMessage;
export const selectCompletionMessage = (state: RootState) => state.event.completionMessage;
export const selectIsImmediateCompletionAttempt = (state: RootState) => state.event.isImmediateCompletionAttempt;
export const selectDeleteCameraErrorMessage = (state: RootState) => state.event.deleteCameraErrorMessage;
export const selectEvent = (state: RootState) => {
  return state.event.events.find((_event) => _event.eventId === state.event.selectedEventId);
};
export const selectEventCompleted = (state: RootState) =>
  ['INITIATED', 'COMPLETE'].includes(selectEvent(state)?.status || '');

export const checkFailedCameraUploads = (state: RootState) => {
  const selectedEvent = state.event.events.find((_event) => _event.eventId === state.event.selectedEventId);
  return selectedEvent?.days?.find((day) => day?.cameras?.find((camera) => camera.status === 'FAILED'));
};

export const selectFailedCameraUploads = (state: RootState) => {
  return state.event.events.find((_event) => _event.eventId === state.event.selectedEventId)?.failedCameraUploads;
};

export const selectValidationErrors = (state: RootState) =>
  state.event.events.find((event) => event.eventId === state.event.selectedEventId)?.validationErrors;

// export actions
export const { resetEventState, resetCompleteStatus } = eventsSlice.actions;

// export reducer
export default eventsSlice.reducer;
